import { format, isValid, parseISO } from 'date-fns';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import type { TextFieldProps } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

export interface RhfDatePickerProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  textFieldProps?: Pick<
    TextFieldProps,
    'placeholder' | 'sx' | 'InputProps' | 'InputLabelProps'
  >;
}

function RhfDatePicker<T extends FieldValues>({
  name,
  label,
  required,
  disabled,
  textFieldProps,
}: RhfDatePickerProps<T>) {
  const { control } = useFormContext<T>();
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: required ? { required: `${label} is required` } : undefined,
  });

  const value =
    typeof field.value === 'string' && field.value.trim() !== ''
      ? (() => {
          const parsed = parseISO(field.value);
          return isValid(parsed) ? parsed : null;
        })()
      : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={value}
        onChange={(date) => {
          field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
        }}
        label={label}
        disabled={disabled}
        format="yyyy-MM-dd"
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          textField: {
            name: field.name,
            onBlur: field.onBlur,
            required,
            fullWidth: true,
            size: 'small',
            variant: 'outlined',
            error: !!error,
            helperText: error?.message,
            ...textFieldProps,
          },
          ...(disabled
            ? { openPickerButton: { sx: { display: 'none' } } }
            : {}),
        }}
      />
    </LocalizationProvider>
  );
}

export default RhfDatePicker;
